import React, { useRef, useState, useMemo } from 'react';
import {
  Animated,
  ScrollView as RNScrollView,
  ScrollViewProps,
  View,
  LayoutChangeEvent,
} from 'react-native';
import ScrollViewStyles from './ScrollView.styles';

interface Props extends ScrollViewProps {
  children: React.ReactNode;
}

const ScrollView: React.FC<Props> = ({
  children,
  style,
  contentContainerStyle,
  ...rest
}) => {
  const scrollY = useRef(new Animated.Value(0)).current;
  const scrollViewRef = useRef<RNScrollView>(null);
  const styles = ScrollViewStyles();
  const [containerHeight, setContainerHeight] = useState(1);
  const [contentHeight, setContentHeight] = useState(1);

  const onLayout = (event: LayoutChangeEvent) => {
    setContainerHeight(event.nativeEvent.layout.height);
  };

  const onContentSizeChange = (_: number, height: number) => {
    setContentHeight(height);
  };

  const isScrollable = contentHeight > containerHeight;

  const thumbHeight = useMemo(() => {
    if (!isScrollable) return 0;
    const ratio = containerHeight / contentHeight;
    return Math.max(ratio * containerHeight, 20);
  }, [containerHeight, contentHeight, isScrollable]);

  const scrollableHeight = Math.max(contentHeight - containerHeight, 1);
  const scrollRange = containerHeight - thumbHeight;

  const translateY = scrollY.interpolate({
    inputRange: [0, scrollableHeight],
    outputRange: [0, scrollRange],
    extrapolate: 'clamp',
  });

  return (
    <View style={style} onLayout={onLayout}>
      <Animated.ScrollView
        ref={scrollViewRef}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { y: scrollY } } }],
          { useNativeDriver: false },
        )}
        onContentSizeChange={onContentSizeChange}
        contentContainerStyle={contentContainerStyle}
        {...rest}
      >
        {children}
      </Animated.ScrollView>
      {isScrollable && (
        <View style={styles.scrollbarTrack} pointerEvents="none">
          <Animated.View
            style={[
              styles.scrollbarThumb,
              {
                height: thumbHeight,
                transform: [{ translateY }],
              },
            ]}
          />
        </View>
      )}
    </View>
  );
};

export default ScrollView;
